// The bit matrix, and what every square in it is for.
//
// ---------------------------------------------------------------------------
// WHY THIS EXISTS AT ALL
// ---------------------------------------------------------------------------
// **jsQR does not expose the grid.** It returns the decoded string, the
// version and four corner points, and everything above that is discarded
// inside it. The x-ray needs to know which specific squares in THIS code are
// dark, so the sampling is ours. That is not duplicated work: it is the half
// that is the product, and it is what unlocks the mask, the format bits, the
// reading order and the damage budget, none of which a decoded string can
// answer.
//
// **Nothing here touches the DOM.** It takes pixels and returns numbers, which
// is what makes it testable without a browser.

// ---------------------------------------------------------------------------
// Where the alignment patterns sit, by version. ISO/IEC 18004 table E.1.
// Version 1 has none; every other version has one at each pairwise combination
// of these coordinates, minus the three that would land on a finder pattern.
// ---------------------------------------------------------------------------
const ALIGNMENT_CENTRES = [
    [], [], [6, 18], [6, 22], [6, 26], [6, 30], [6, 34],
    [6, 22, 38], [6, 24, 42], [6, 26, 46], [6, 28, 50], [6, 30, 54],
    [6, 32, 58], [6, 34, 62], [6, 26, 46, 66], [6, 26, 48, 70],
    [6, 26, 50, 74], [6, 30, 54, 78], [6, 30, 56, 82], [6, 30, 58, 86],
    [6, 34, 62, 90], [6, 28, 50, 72, 94], [6, 26, 50, 74, 98],
    [6, 30, 54, 78, 102], [6, 28, 54, 80, 106], [6, 32, 58, 84, 110],
    [6, 30, 58, 86, 114], [6, 34, 62, 90, 118], [6, 26, 50, 74, 98, 122],
    [6, 30, 54, 78, 102, 126], [6, 26, 52, 78, 104, 130],
    [6, 30, 56, 82, 108, 134], [6, 34, 60, 86, 112, 138],
    [6, 30, 58, 86, 114, 142], [6, 34, 62, 90, 118, 146],
    [6, 30, 54, 78, 102, 126, 150], [6, 24, 50, 76, 102, 128, 154],
    [6, 28, 54, 80, 106, 132, 158], [6, 32, 58, 84, 110, 136, 162],
    [6, 26, 54, 82, 110, 138, 166], [6, 30, 58, 86, 114, 142, 170],
];

// What a square is FOR. Anything not listed is data.
export const ROLE = {
    DATA: 0, FINDER: 1, SEPARATOR: 2, TIMING: 3,
    ALIGNMENT: 4, FORMAT: 5, VERSION: 6, DARK: 7,
};

export function modulesPerSide(version) { return (version * 4) + 17; }

// ---------------------------------------------------------------------------
// The projective transform from the unit square to the four located corners.
//
// **Affine is not enough and the difference is the whole point.** A photograph
// of a poster is a quadrilateral, not a parallelogram; an affine map would
// sample progressively further from the module centres across the code and
// fall apart on exactly the angled shots this page exists to handle.
// ---------------------------------------------------------------------------
function squareToQuad(p0, p1, p2, p3) {
    const dx1 = p1.x - p2.x, dx2 = p3.x - p2.x, dx3 = p0.x - p1.x + p2.x - p3.x;
    const dy1 = p1.y - p2.y, dy2 = p3.y - p2.y, dy3 = p0.y - p1.y + p2.y - p3.y;

    if (dx3 === 0 && dy3 === 0) {
        return { a: p1.x - p0.x, b: p2.x - p1.x, c: p0.x,
                 d: p1.y - p0.y, e: p2.y - p1.y, f: p0.y, g: 0, h: 0 };
    }

    const denominator = dx1 * dy2 - dx2 * dy1;
    const g = (dx3 * dy2 - dx2 * dy3) / denominator;
    const h = (dx1 * dy3 - dx3 * dy1) / denominator;

    return {
        a: p1.x - p0.x + g * p1.x, b: p3.x - p0.x + h * p3.x, c: p0.x,
        d: p1.y - p0.y + g * p1.y, e: p3.y - p0.y + h * p3.y, f: p0.y,
        g, h,
    };
}

function project(t, u, v) {
    const w = t.g * u + t.h * v + 1;
    return { x: (t.a * u + t.b * v + t.c) / w, y: (t.d * u + t.e * v + t.f) / w };
}

// ---------------------------------------------------------------------------
// Sampling
// ---------------------------------------------------------------------------

// A 3x3 average rather than a single pixel: one pixel at a module centre is at
// the mercy of JPEG ringing and of a grid that is half a pixel out.
function luminanceAt(pixels, width, height, x, y) {
    let total = 0, count = 0;
    for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
            const px = Math.round(x) + dx, py = Math.round(y) + dy;
            if (px < 0 || py < 0 || px >= width || py >= height) { continue; }
            const i = ((py * width) + px) * 4;
            total += (pixels[i] * 0.299) + (pixels[i + 1] * 0.587) + (pixels[i + 2] * 0.114);
            count++;
        }
    }
    return count ? total / count : 255;
}

// **Otsu over the sampled values, not over the picture.** The threshold that
// matters is the one separating THIS code's dark modules from its light ones,
// and a photograph usually contains a great deal that is neither.
function otsu(values) {
    const histogram = new Array(256).fill(0);
    values.forEach(v => histogram[Math.max(0, Math.min(255, Math.round(v)))]++);

    const total = values.length;
    let sum = 0;
    for (let i = 0; i < 256; i++) { sum += i * histogram[i]; }

    let sumBackground = 0, weightBackground = 0, best = 0, threshold = 127;

    for (let i = 0; i < 256; i++) {
        weightBackground += histogram[i];
        if (weightBackground === 0) { continue; }
        const weightForeground = total - weightBackground;
        if (weightForeground === 0) { break; }

        sumBackground += i * histogram[i];
        const meanBackground = sumBackground / weightBackground;
        const meanForeground = (sum - sumBackground) / weightForeground;
        const between = weightBackground * weightForeground
            * (meanBackground - meanForeground) * (meanBackground - meanForeground);

        if (between > best) { best = between; threshold = i; }
    }

    return threshold;
}

/**
 * Sample the module grid out of the image, using the corners jsQR located.
 * Returns { size, modules } where modules is 1 for dark and 0 for light.
 */
export function sampleGrid(pixels, width, height, location, version) {
    const size = modulesPerSide(version);

    const transform = squareToQuad(
        location.topLeftCorner, location.topRightCorner,
        location.bottomRightCorner, location.bottomLeftCorner);

    const values = new Float32Array(size * size);

    for (let row = 0; row < size; row++) {
        for (let column = 0; column < size; column++) {
            const point = project(transform, (column + 0.5) / size, (row + 0.5) / size);
            values[(row * size) + column] = luminanceAt(pixels, width, height, point.x, point.y);
        }
    }

    const threshold = otsu(Array.from(values));
    const modules = new Uint8Array(size * size);

    // **`<=`, not `<`, and the difference is total.** Otsu returns the last
    // level belonging to the DARK class, so the split is [0..t] against
    // [t+1..255]. With `<` a clean black-and-white code, whose sampled values
    // are exactly 0 and 255, produces a threshold of 0 and then not one dark
    // square: the grid comes back entirely blank and the confidence check
    // refuses to draw it. Found by running the whole pipeline headlessly
    // against a code this app rendered itself.
    for (let i = 0; i < values.length; i++) { modules[i] = values[i] <= threshold ? 1 : 0; }

    return { size, modules };
}

// ---------------------------------------------------------------------------
// What every square is for. Entirely determined by the version, so this is a
// map of the FORMAT rather than of any particular code.
// ---------------------------------------------------------------------------
export function classify(version) {
    const size = modulesPerSide(version);
    const roles = new Uint8Array(size * size);
    const set = (row, column, role) => {
        if (row >= 0 && column >= 0 && row < size && column < size) {
            roles[(row * size) + column] = role;
        }
    };

    // Three finders with their separators, at three corners. Never the fourth:
    // that absence is how a reader works out which way up the code is.
    [[0, 0], [0, size - 7], [size - 7, 0]].forEach(([top, left]) => {
        for (let row = -1; row <= 7; row++) {
            for (let column = -1; column <= 7; column++) {
                const inside = row >= 0 && row <= 6 && column >= 0 && column <= 6;
                set(top + row, left + column, inside ? ROLE.FINDER : ROLE.SEPARATOR);
            }
        }
    });

    // The timing lines: the ruler a reader uses to count modules.
    for (let i = 8; i < size - 8; i++) { set(6, i, ROLE.TIMING); set(i, 6, ROLE.TIMING); }

    // Alignment patterns, at every pairwise combination except the three that
    // would sit on a finder.
    const centres = ALIGNMENT_CENTRES[version] || [];
    centres.forEach(row => centres.forEach(column => {
        const onFinder = (row === 6 && column === 6)
            || (row === 6 && column === size - 7) || (row === size - 7 && column === 6);
        if (onFinder) { return; }
        for (let dy = -2; dy <= 2; dy++) {
            for (let dx = -2; dx <= 2; dx++) { set(row + dy, column + dx, ROLE.ALIGNMENT); }
        }
    }));

    // Format information, stored twice so a damaged corner cannot lose it.
    for (let i = 0; i <= 8; i++) {
        if (i !== 6) { set(8, i, ROLE.FORMAT); set(i, 8, ROLE.FORMAT); }
    }
    for (let i = 0; i < 8; i++) {
        set(8, size - 1 - i, ROLE.FORMAT);
        set(size - 1 - i, 8, ROLE.FORMAT);
    }

    // Version information, from version 7 up, also twice.
    if (version >= 7) {
        for (let i = 0; i < 6; i++) {
            for (let j = 0; j < 3; j++) {
                set(i, size - 11 + j, ROLE.VERSION);
                set(size - 11 + j, i, ROLE.VERSION);
            }
        }
    }

    // The one module that is always dark, and carries nothing.
    set(size - 8, 8, ROLE.DARK);

    return { size, roles };
}

// ---------------------------------------------------------------------------
// The reading order: the zigzag that IS "dot to dot".
//
// Two-module columns walked right to left, alternating upward and downward,
// stepping over the vertical timing line. Within a row of a column pair the
// right module comes before the left. Function squares are skipped, so what
// comes back is the data path and nothing else.
// ---------------------------------------------------------------------------
export function dataPath(version) {
    const { size, roles } = classify(version);
    const path = [];
    let upward = true;

    for (let right = size - 1; right > 0; right -= 2) {
        if (right === 6) { right--; }              // the timing column is not a column

        for (let step = 0; step < size; step++) {
            const row = upward ? size - 1 - step : step;
            for (const column of [right, right - 1]) {
                if (roles[(row * size) + column] === ROLE.DATA) {
                    path.push({ row, column });
                }
            }
        }

        upward = !upward;
    }

    return path;
}

// ---------------------------------------------------------------------------
// **Did the sampling actually work?**
//
// The finder patterns are the same seven-by-seven shape in every QR code ever
// made, and the timing lines alternate by definition. So a sampled grid can be
// checked against something known rather than trusted: if these do not come
// out right, the transform is wrong and everything drawn from it would be a
// plausible fiction. Reported rather than thrown, because the honest answer to
// the reader is that the picture was too poor to take apart.
// ---------------------------------------------------------------------------
export function gridLooksRight(modules, size) {
    const at = (row, column) => modules[(row * size) + column];
    let checked = 0, correct = 0;

    const FINDER = [
        [1, 1, 1, 1, 1, 1, 1],
        [1, 0, 0, 0, 0, 0, 1],
        [1, 0, 1, 1, 1, 0, 1],
        [1, 0, 1, 1, 1, 0, 1],
        [1, 0, 1, 1, 1, 0, 1],
        [1, 0, 0, 0, 0, 0, 1],
        [1, 1, 1, 1, 1, 1, 1],
    ];

    for (const [top, left] of [[0, 0], [0, size - 7], [size - 7, 0]]) {
        for (let row = 0; row < 7; row++) {
            for (let column = 0; column < 7; column++) {
                checked++;
                if (at(top + row, left + column) === FINDER[row][column]) { correct++; }
            }
        }
    }

    for (let i = 8; i < size - 8; i++) {
        checked += 2;
        if (at(6, i) === (i % 2 === 0 ? 1 : 0)) { correct++; }
        if (at(i, 6) === (i % 2 === 0 ? 1 : 0)) { correct++; }
    }

    return correct / checked;
}
